<template>
    <section id="PatientInfo__Main">
        <div>
            <section class="header-row align-items-center fw-bold">
                <span class="fw-bold user-name font-size-xl me-2">
                    <a href="javascript:void(0);" @click="handlePatientNameClick()">
                        {{
                            formatName(
                                selectedPatientDetails.firstName,
                                selectedPatientDetails.lastName,
                                "FN LN",
                                "",
                                selectedPatientDetails.middleInitial
                            )
                        }}
                    </a>
                </span>
                <span class="fw-bold mt-sm-1 user-mrn" v-tooltip="{ title: 'Medical Record Number (MRN)' }">{{
                    selectedPatientDetails.medicalRecordNumber
                }}</span>
            </section>
            <section class="header-row fw-bold font-size-xs">
                <span class="header-cell" v-tooltip="{ title: 'Sex' }">
                    <span v-if="selectedPatientDetails.gender">
                        {{ genderEnum.getDescriptionUsingValue(selectedPatientDetails.gender) }}
                    </span>
                    <span class="text-default" v-else>No Gender Selected</span>
                </span>
                <span class="header-cell" v-tooltip="{ title: `Patient's Age` }">
                    <i :class="`fas fa-circle header-divider `"></i>
                    <span v-if="age">
                        {{ age }}
                    </span>
                    <span class="text-default" v-else>Age not available</span>
                </span>
                <span class="header-cell" v-tooltip="{ title: `Level of Care` }" v-if="!isPalliativeCare">
                    <i :class="`fas fa-circle header-divider `"></i>

                    <span v-if="selectedPatientDetails.levelOfCare">
                        {{ levelOfCareEnum.getDescriptionUsingValue(selectedPatientDetails.levelOfCare) }}
                    </span>
                    <span class="text-default" v-else>No Level of Care Selected</span>
                </span>
                <span class="header-cell" v-tooltip="{ title: veteranLabel }" v-if="veteranLabel">
                    <i :class="`fas fa-circle header-divider `"></i>
                    <span> Veteran </span>
                </span>
                <span class="header-cell">
                    <i :class="`fas fa-circle header-divider `"></i>
                    <div>
                        <span class="badge badge-warning" v-if="selectedPatientDetails.isHospitalized">
                            Hospitalized
                        </span>
                        <span
                            class="badge CodeStatus__Red"
                            tooltip="title: Do Not Resuscitate"
                            v-if="isDoNotResuscitate"
                            >DNR</span
                        >
                        <span class="badge CodeStatus__Red" tooltip="title: Do Not Intubate" v-if="isDoNotIntubate">
                            DNI
                        </span>
                        <span
                            class="badge CodeStatus__Red"
                            tooltip="title: Do Not Hospitalize"
                            v-if="isDoNotHospitalize"
                        >
                            DNH
                        </span>
                        <span
                            class="badge CodeStatus__Green"
                            v-if="!isDoNotResuscitate && !isDoNotIntubate && !isDoNotHospitalize"
                        >
                            Full Code
                        </span>
                        <span
                            class="badge CodeStatus__Red"
                            tooltip="title: Other Code Status"
                            v-if="!!selectedPatientDetails.other"
                            >{{ selectedPatientDetails.other }}
                        </span>
                    </div>
                </span>
                <span class="header-cell" v-if="!!selectedPatientDetails.facilityName">
                    <i :class="`fas fa-circle header-divider `"></i>
                    {{ selectedPatientDetails.facilityName }}
                </span>
                <span class="header-cell" v-if="isTagsEnabled && patientTags">
                    <i :class="`fas fa-circle header-divider `"></i>
                    <span v-tooltip="{ title: `${patientTags}` }"> Tags </span>
                </span>
                <span class="header-cell" v-if="!isPalliativeCare">
                    <i :class="`fas fa-circle header-divider `"></i>
                    <a
                        :href="`/patients/allergy-profile/${selectedPatientDetails.id}?status=${selectedPatientDetails.status}`"
                        class="font-size-xs font-weight-bold text-danger"
                    >
                        {{ allergyCount > 0 ? `Allergies (${allergyCount})` : "No Known Allergies" }}
                    </a>
                </span>
                <!-- <span class="header-cell" v-if="isPalliativeCare || patientSpecialtyPrograms">
                    <i class="fas fa-circle header-divider"></i>

                    <span v-tooltip="{ title: `${patientSpecialtyPrograms}` }"> Specialty Program </span>
                </span> -->
            </section>
            <section class="header-row fw-bold font-size-xs">
                <section class="header-cell" v-tooltip="{ title: `Patient's Address` }">
                    <i :class="`fas fa-map-marker-alt me-1 text-app-secondary-${isPalliativeCare ? 'pal' : 'hos'}`"></i>
                    <a v-if="hasAddress" :href="patientAddressLink" target="_blank">
                        {{ displayAddress }}
                    </a>
                    <span v-else class="text-default">Address Not Available</span>
                </section>
                <section class="header-cell" v-tooltip="{ title: `Patient's Phone Number` }">
                    <i :class="`fas fa-circle header-divider `"></i>
                    <i :class="`fas fa-phone-square me-1 text-app-secondary-${isPalliativeCare ? 'pal' : 'hos'}`"></i>

                    <a
                        :href="`tel: ${selectedPatientDetails.preferredPhone}`"
                        v-if="selectedPatientDetails.preferredPhone"
                    >
                        {{ formatPhoneNumber(selectedPatientDetails.preferredPhone) }}
                    </a>
                    <span v-else class="text-default">Phone Not Available</span>
                </section>
                <section class="header-cell" :tooltip="{ title: `Patient's Date of Birth` }">
                    <i :class="`fas fa-circle header-divider `"></i>
                    <i :class="`fas fa-birthday-cake me-1 text-app-secondary-${isPalliativeCare ? 'pal' : 'hos'}`"></i>

                    <span v-if="selectedPatientDetails.dateOfBirth">{{
                        formatDate(selectedPatientDetails.dateOfBirth)
                    }}</span>
                    <span v-else class="text-default">D.O.B Not Available</span>
                </section>
            </section>
            <section id="headerDeskInfo" class="header-row mt-1">
                <div class="information-column d-flex flex-column me-4" v-if="!isPalliativeCare">
                    <section class="information-cell">
                        <header class="title text-nowrap">
                            <b>Benefit Period Details</b>
                            <a
                                v-if="!isPendingPatient && canShowBenefitPeriodManager"
                                :href="`/patients/benefit-period-manager/${selectedPatientDetails.id}?status=${selectedPatientDetails.status}`"
                                class="font-size-xs ms-2 font-weight-bold"
                            >
                                View
                            </a>
                            <span v-if="isPendingPatient">
                                <span class="text-default">Pending</span>
                            </span>
                            <span v-else>
                                <div class="text-nowrap">
                                    <span
                                        class="badge badge-light me-2"
                                        v-if="selectedPatientDetails.benefitPeriodNumber"
                                        ># {{ selectedPatientDetails.benefitPeriodNumber }}</span
                                    >
                                    <span
                                        v-if="
                                            (selectedPatientDetails.benefitPeriodStartDate ||
                                                selectedPatientDetails.transferBenefitPeriodStartDate) &&
                                            selectedPatientDetails.benefitPeriodEndDate
                                        "
                                    >
                                        <span v-if="selectedPatientDetails.isTransfer">{{
                                            formatDate(selectedPatientDetails.transferBenefitPeriodStartDate)
                                        }}</span>
                                        <span v-else>{{
                                            formatDate(selectedPatientDetails.benefitPeriodStartDate)
                                        }}</span>
                                        <span class="mx-auto">-</span>
                                        <span>{{ formatDate(selectedPatientDetails.benefitPeriodEndDate) }}</span>
                                    </span>
                                    <span class="text-default" v-else>Not Available</span>
                                </div>
                            </span>
                        </header>
                    </section>
                    <section class="information-cell">
                        <b class="title">Attending Physician</b>
                        <div v-if="selectedPatientDetails.attendingPhysicianName">
                            {{ selectedPatientDetails.attendingPhysicianName }}
                        </div>
                        <span class="text-default" v-else>Not Available</span>
                    </section>
                </div>
                <div class="information-column d-flex flex-column me-4">
                    <section class="information-cell">
                        <header class="title">
                            <b class="title">Admission Date</b>
                            <a
                                v-if="!isPendingPatient && canShowBenefitPeriodManager && isPalliativeCare"
                                :href="`/patients/admission-period-manager/${selectedPatientDetails.id}?status=${selectedPatientDetails.status}`"
                                class="font-size-xs ms-2 font-weight-bold"
                            >
                                View
                            </a>
                        </header>
                        <div>
                            <span v-if="selectedPatientDetails.admissionDate">
                                {{ formatDate(selectedPatientDetails.admissionDate) }}
                            </span>
                            <div v-else>
                                <span class="text-default">
                                    {{ isPendingPatient ? "Pending" : "Not Available" }}
                                </span>
                            </div>
                        </div>
                    </section>
                    <section class="information-cell" v-if="!isPalliativeCare">
                        <b class="title">Medicare Number</b>
                        <div>
                            <span v-if="selectedPatientDetails.medicareBeneficiaryIdentification">
                                {{ selectedPatientDetails.medicareBeneficiaryIdentification }}
                                <span
                                    v-if="selectedPatientDetails.isPayerVerified"
                                    class="fas fa-check-circle ready text-success align-self-center ms-1"
                                    v-tooltip="{ title: `Benefits have been verified for this payer.` }"
                                ></span>
                                <span
                                    v-else
                                    class="fas fa-times-circle warn text-danger ms-1 align-self-center ms-1"
                                    v-tooltip="{ title: `Benefits have not been verified for this payer.` }"
                                ></span>
                            </span>
                            <span class="text-default" v-else>Not Available</span>
                        </div>
                    </section>
                    <section class="information-cell" v-if="isPalliativeCare">
                        <b class="title">{{ selectedPatientDetails.primaryPayor?.PayorName }}</b>
                        <div>
                            <span class="d-flex">
                                <EmptyTextHandler
                                    :text="selectedPatientDetails.primaryPayor?.MemberId"
                                ></EmptyTextHandler>
                                <span
                                    v-tooltip="{
                                        title: `${selectedPatientDetails.primaryPayor?.IsBenefitVerified ? 'Benefits have been verified for this payer.' : 'Benefits have not been verified for this payer.'}  
                            `
                                    }"
                                    :class="`text-center fas ${selectedPatientDetails.primaryPayor?.IsBenefitVerified ? 'fa-check-circle text-success' : 'fa-times-circle text-danger'} ms-1`"
                                ></span>
                            </span>
                        </div>
                    </section>
                </div>
                <div class="information-column h-100 d-flex flex-column me-4" v-if="!isPalliativeCare">
                    <section class="information-cell">
                        <b class="title"><span v-if="!isPalliativeCare">Terminal </span>Diagnosis</b>
                        <div>
                            <span
                                class="text-truncate"
                                v-tooltip="{ title: `${terminalDiagTooltip}` }"
                                v-if="!!terminalDiagnosis"
                            >
                                {{ terminalDiagnosis.description }}
                            </span>
                            <span class="text-default" v-else>Not Available</span>
                        </div>
                    </section>
                    <section class="information-cell">
                        <b class="title">Medicaid Number</b>
                        <div>
                            <span v-if="medicaidLabel">
                                {{ medicaidLabel }}
                            </span>
                            <span class="text-default" v-else>Not Available</span>
                        </div>
                    </section>
                </div>
                <div class="information-column h-100 me-4" v-if="isPalliativeCare">
                    <section class="information-cell">
                        <b class="title">Diagnosis</b>
                        <div>
                            <span
                                class="text-truncate"
                                v-tooltip="{ title: `${terminalDiagTooltip}` }"
                                v-if="!!terminalDiagnosis"
                            >
                                {{ terminalDiagnosis.description }}
                            </span>
                            <span class="text-default" v-else>Not Available</span>
                        </div>
                    </section>
                    <section class="information-cell">
                        <b class="title">Palliative Provider</b>
                        <div>
                            <span v-if="selectedPatientDetails.attendingPhysicianName">
                                {{ selectedPatientDetails.attendingPhysicianName }}
                            </span>
                            <span class="text-default" v-else>Not Available</span>
                        </div>
                    </section>
                </div>
                <div class="information-column h-100">
                    <section class="information-cell" v-if="isDischarged">
                        <b class="title">Discharge Date</b>
                        <div>
                            <span v-if="selectedPatientDetails.dischargeDate">
                                {{ formatDate(selectedPatientDetails.dischargeDate) }}
                            </span>
                            <span class="text-default" v-else>Not Available</span>
                        </div>
                    </section>
                </div>
            </section>
        </div>
    </section>
</template>

<style lang="sass" src="./HosPalPatientMain.scss"></style>
<script lang="ts" src="./HosPalPatientMain.ts"></script>
